import { Brain } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseMetricLogCard } from "@/core/components/tracking";
import { updateStressLevel } from "@/lib/api";
import { useProductContext } from "@/contexts/ProductContext";

interface StressLogCardProps {
  onComplete?: () => void;
  onDataUpdate?: () => void;
  isLoading?: boolean;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}

export function StressLogCard({ onComplete, onDataUpdate, isLoading, externalOpen, onExternalOpenChange }: StressLogCardProps) {
  const { t } = useTranslation();
  const { currentProduct } = useProductContext();

  return (
    <BaseMetricLogCard
      // Display
      icon={Brain}
      gradientClasses="from-purple-500 to-indigo-600"
      borderHoverClasses="hover:border-purple-500/40"

      // Input type - using slider for 1-10 scale
      inputType="slider"
      sliderConfig={{
        min: 1,
        max: 10,
        step: 1,
        defaultValue: 5,
        showScale: true,
        scaleLabels: ["😌", "😊", "🙂", "😐", "😕", "😟", "😥", "😰", "😫", "🤯"],
      }}

      // Data field
      fieldName="stress_level"

      // Validation
      minValue={1}
      maxValue={10}

      // Pre-translated strings
      cardTitle={t('mindco-relief:actions.logStress', 'Log Stress')}
      cardDescription={t('mindco-relief:actions.stressHelp', 'Rate your current stress level')}
      dialogTitle={t('mindco-relief:actions.logStress', 'Log Stress')}
      dialogDescription={t('mindco-relief:actions.stressHelp', 'Rate your current stress level')}
      inputLabel={t('mindco-relief:actions.levelFor', 'Level for')}
      inputPlaceholder="1-10"
      scaleDescription={t('mindco-relief:actions.stressScale', '1 = Very calm, 10 = Extremely stressed')}
      notesPlaceholder={t('mindco-relief:actions.notesPlaceholder', 'Any notes about how you\'re feeling...')}
      loggedTitle={t('mindco-relief:actions.stressLogged', 'Stress Level Logged')}
      loggedDescription={t('mindco-relief:actions.stressLoggedDescription', 'Your stress level has been recorded.')}
      errorInvalidInput={t('common:errors.invalidInput', 'Invalid Input')}
      errorInvalidValue={t('mindco-relief:errors.invalidStressLevel', 'Please enter a valid stress level (1-10).')}

      // Analytics
      analyticsEvent="Stress Level Logged"

      // Popup triggers
      popupTriggers={[
        { trigger: "stress_logged" },
        {
          trigger: "high_stress_logged",
          condition: (value) => value >= 7,
          contextMapper: (value) => ({ level: value })
        }
      ]}

      // API function
      updateFunction={updateStressLevel}

      // Callbacks
      onComplete={onComplete}
      onDataUpdate={onDataUpdate}
      isLoading={isLoading}
      externalOpen={externalOpen}
      onExternalOpenChange={onExternalOpenChange}

      // Product context
      productId={currentProduct}
    />
  );
}
